import { Link } from "@remix-run/react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "~/lib/auth-context";
import { useNavigation, navigationConfig } from "~/lib/navigation-utils";
import { NavigationIcon } from "~/components/ui/NavigationIcon";
import { AuthAwareLink } from "~/components/ui/AuthAwareLink";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user } = useAuth();
  const { isActive } = useNavigation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const currentXRef = useRef<number>(0);

  // Show all navigation items except admin-only items for non-admin users
  const visibleNavItems = navigationConfig.filter((item) => {
    // Only hide admin items for non-admin users
    if (item.adminOnly && user?.profile?.role !== "admin") {
      return false;
    }
    return true;
  });

  // Touch gesture support for swipe to close
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar || !isOpen) return;

    const handleTouchStart = (e: TouchEvent) => {
      startXRef.current = e.touches[0].clientX;
      currentXRef.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: TouchEvent) => {
      currentXRef.current = e.touches[0].clientX;
      const deltaX = currentXRef.current - startXRef.current;

      // Only allow swipe left to close
      if (deltaX < 0) {
        const translateX = Math.max(deltaX, -256); // Max swipe distance
        sidebar.style.transform = `translateX(${translateX}px)`;
      }
    };

    const handleTouchEnd = () => {
      const deltaX = currentXRef.current - startXRef.current;

      // If swiped more than 50px to the left, close sidebar
      if (deltaX < -50) {
        onClose();
      }

      // Reset transform
      sidebar.style.transform = "";
    };

    sidebar.addEventListener("touchstart", handleTouchStart, { passive: true });
    sidebar.addEventListener("touchmove", handleTouchMove, { passive: true });
    sidebar.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      sidebar.removeEventListener("touchstart", handleTouchStart);
      sidebar.removeEventListener("touchmove", handleTouchMove);
      sidebar.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out z-30 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar header */}
          <div className="h-16 flex items-center justify-between px-4 border-b dark:border-gray-700">
            <Link to="/" className="flex items-center" onClick={onClose}>
              <img
                src="/logo-light.png"
                alt="Slowdive"
                className="h-12 w-auto mr-2 block dark:hidden"
                width={48}
                height={48}
                loading="eager"
                decoding="async"
              />
              <img
                src="/logo-dark.png"
                alt="Slowdive"
                className="h-12 w-auto mr-2 hidden dark:block"
                width={48}
                height={48}
                loading="eager"
                decoding="async"
              />
              {/* <span className="text-xl font-bold text-gray-900">slowdive</span> */}
            </Link>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none"
            >
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-y-auto">
            <nav className="px-2 py-4 space-y-1">
              {visibleNavItems.map((item) => {
                const active = isActive(item);

                return (
                  <div key={item.id}>
                    <AuthAwareLink
                      to={item.href}
                      requiresAuth={item.requiresAuth}
                      authMessage={item.authRedirectMessage}
                      prefetch="intent"
                      onClick={onClose}
                      className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors cursor-pointer w-full ${
                        active
                          ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-400"
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      {item.iconName && (
                        <NavigationIcon
                          name={item.iconName}
                          className={`mr-4 h-6 w-6 ${
                            active
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-gray-500 group-hover:text-gray-600 dark:text-gray-400 dark:group-hover:text-gray-300"
                          }`}
                        />
                      )}
                      {item.label}
                    </AuthAwareLink>

                    {/* Render children if they exist and parent is active */}
                    {item.children && active && (
                      <div className="ml-6 mt-1 space-y-1">
                        {item.children.map((child) => {
                          const childActive = isActive(child);
                          return (
                            <AuthAwareLink
                              key={child.id}
                              to={child.href}
                              requiresAuth={child.requiresAuth}
                              authMessage={child.authRedirectMessage}
                              prefetch="intent"
                              onClick={onClose}
                              className={`block px-2 py-1 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                                childActive
                                  ? "text-blue-700 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20"
                                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                              }`}
                            >
                              {child.label}
                            </AuthAwareLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Sidebar footer */}
          <div className="p-4 border-t ">
            {user ? (
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
                    <span className="text-gray-600 dark:text-gray-300 font-medium">
                      {user.profile?.name?.[0] || user.email?.[0] || "U"}
                    </span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user.profile?.name || user.email}
                  </p>
                  <Link
                    to="/logout"
                    className="text-xs font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    onClick={onClose}
                  >
                    로그아웃
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                  onClick={onClose}
                >
                  로그인
                </Link>
                <Link
                  to="/signup"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:text-gray-900 dark:bg-blue-400 dark:hover:bg-blue-500"
                  onClick={onClose}
                >
                  회원가입
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
